import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

function PatientAiSummary({ practiceId, patientId, context = 'detail' }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSummary = useCallback(async (refresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getPatientAiSummary(practiceId, patientId, context, refresh);
      if (res.success) {
        setSummary(res.data);
      } else {
        setError(res.error || 'Failed to load summary');
      }
    } catch (err) {
      setError(err.message || 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  }, [practiceId, patientId, context]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Generating AI summary...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorText}>{error}</p>
        <button onClick={() => loadSummary(true)} style={styles.retryBtn}>Retry</button>
      </div>
    );
  }

  if (!summary) return null;

  const scoreColor = summary.engagement_score >= 70 ? '#10b981' :
                     summary.engagement_score >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={{ ...styles.scoreBadge, backgroundColor: scoreColor }}>
            {summary.engagement_score}
          </div>
          <div>
            <div style={styles.scoreLabel}>Engagement Score</div>
            <div style={styles.generatedAt}>
              {summary.cached && <span style={styles.cachedBadge}>Cached</span>}
              {summary.fallback && <span style={styles.fallbackBadge}>Basic</span>}
              {summary.generated_at && <span>Generated {new Date(summary.generated_at).toLocaleString()}</span>}
            </div>
          </div>
        </div>
        <button onClick={() => loadSummary(true)} style={styles.refreshBtn} title="Refresh summary">
          Refresh
        </button>
      </div>

      {/* Overview */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Overview</div>
        <p style={styles.sectionText}>{summary.overview}</p>
      </div>

      {/* Channel Insights */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Channel Insights</div>
        <p style={styles.sectionText}>{summary.channel_insights}</p>
      </div>

      {/* Sentiment Trend */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Sentiment Trend</div>
        <p style={styles.sectionText}>{summary.sentiment_trend}</p>
      </div>

      {/* Campaign Status */}
      {summary.campaign_status && summary.campaign_status !== 'No campaign data.' && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Campaign Status</div>
          <p style={styles.sectionText}>{summary.campaign_status}</p>
        </div>
      )}

      {/* Risk Flags */}
      {summary.risk_flags && summary.risk_flags.length > 0 && (
        <div style={{ ...styles.section, ...styles.riskSection }}>
          <div style={styles.sectionTitle}>Risk Flags</div>
          <ul style={styles.list}>
            {summary.risk_flags.map((flag, i) => (
              <li key={i} style={styles.riskItem}>{flag}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommended Actions */}
      {summary.recommended_actions && summary.recommended_actions.length > 0 && (
        <div style={{ ...styles.section, ...styles.actionSection }}>
          <div style={styles.sectionTitle}>Recommended Actions</div>
          <ul style={styles.list}>
            {summary.recommended_actions.map((action, i) => (
              <li key={i} style={styles.actionItem}>{action}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '16px'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 16px'
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e0e0e0',
    borderTop: '3px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loadingText: {
    marginTop: '12px',
    color: '#6b7280',
    fontSize: '13px'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px 16px'
  },
  errorText: {
    color: '#ef4444',
    fontSize: '13px',
    marginBottom: '12px'
  },
  retryBtn: {
    padding: '6px 16px',
    fontSize: '13px',
    color: '#667eea',
    background: 'transparent',
    border: '1px solid #667eea',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid #e5e7eb'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  scoreBadge: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '18px',
    fontWeight: '700'
  },
  scoreLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827'
  },
  generatedAt: {
    fontSize: '11px',
    color: '#9ca3af',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '2px'
  },
  cachedBadge: {
    padding: '1px 6px',
    fontSize: '10px',
    fontWeight: '500',
    color: '#6366f1',
    background: '#e0e7ff',
    borderRadius: '4px'
  },
  fallbackBadge: {
    padding: '1px 6px',
    fontSize: '10px',
    fontWeight: '500',
    color: '#f59e0b',
    background: '#fef3c7',
    borderRadius: '4px'
  },
  refreshBtn: {
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#667eea',
    background: '#f0f0ff',
    border: '1px solid #c7d2fe',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  section: {
    marginBottom: '16px',
    padding: '12px',
    background: '#f9fafb',
    borderRadius: '8px'
  },
  sectionTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '6px'
  },
  sectionText: {
    fontSize: '13px',
    color: '#374151',
    lineHeight: '1.5',
    margin: 0
  },
  riskSection: {
    background: '#fef2f2',
    border: '1px solid #fecaca'
  },
  actionSection: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0'
  },
  list: {
    margin: 0,
    paddingLeft: '20px'
  },
  riskItem: {
    fontSize: '13px',
    color: '#dc2626',
    marginBottom: '4px'
  },
  actionItem: {
    fontSize: '13px',
    color: '#16a34a',
    marginBottom: '4px'
  }
};

export default PatientAiSummary;
